/**
 * The dashboard's chart vocabulary.
 *
 * These live together because the dashboard's credibility depends on its charts
 * agreeing with each other: the same day series drawn the same way, the same
 * hue meaning the same thing in the Tasks section as in the Games section. Four
 * ad-hoc charts written inline drift apart within a release.
 *
 * ── Why this replaces the hand-rolled SVG ──────────────────────────────────
 *
 * The version this supersedes drew bars as flex divs and the points series as
 * an overlaid `<polyline>` on a **second y-axis**. That is the single most
 * misleading thing a chart can do: two independent scales stretched over one
 * plot area make the reader see a correlation that is an artefact of how the
 * two maxima happened to land. On real data it was reading as "points track
 * plays almost exactly", which is not a finding — it is the scaling.
 *
 * Two measures of different units therefore get **two plots sharing one x-axis**
 * instead. Stacked vertically and aligned, a real divergence (a day with many
 * completions but few points, meaning the cheap tasks ran) is visible as a
 * difference in the two shapes, and no false precision is implied.
 *
 * ── The palette ────────────────────────────────────────────────────────────
 *
 * Slots 1–3 of a categorical palette validated for colour-vision deficiency:
 * every adjacent pair clears ΔE 9.2 under deuteranopia (target ≥ 8) and 27.6
 * for normal vision (floor ≥ 15). They are assigned **by entity, not by rank** —
 * blue is always the count, orange is always the points, aqua is always people.
 * A filter that reorders the data must never repaint them.
 *
 * Aqua sits below 3:1 against white, so anything drawn in it carries a visible
 * label or an axis value rather than relying on the fill to be read.
 */

import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Filler, Tooltip, Legend);

/** Categorical slots, assigned by entity and never by rank. */
export const SERIES = {
  count: "#2a78d6", // blue   — completions, plays
  points: "#eb6834", // orange — event points
  people: "#1baf7a", // aqua   — distinct users/players
};

/** Chrome. One step off the surface, so the data stays the loudest thing. */
const INK = {
  muted: "#898781",
  grid: "#e1e0d9",
};

/** `1,284` · `12.9k` · `4.2m` — the axis and tile contract. */
export const compact = (value) => {
  const num = Number(value) || 0;
  if (Math.abs(num) >= 1000000) return `${Math.round(num / 100000) / 10}m`;
  if (Math.abs(num) >= 1000) return `${Math.round(num / 100) / 10}k`;
  return String(Math.round(num));
};

/** "07 Sep" from `YYYY-MM-DD`, without going through a timezone. */
export const shortDay = (iso) => {
  const [, month, day] = String(iso).split("-");
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${day} ${months[Number(month) - 1] || ""}`.trim();
};

/**
 * The tooltip, shared by every chart here.
 *
 * `mode: "index"` means the reader aims at a *day* rather than at a 2px mark,
 * and gets every series for that day at once — so a value is never gated behind
 * landing precisely on the one bar that carries it.
 */
const tooltip = (unit) => ({
  enabled: true,
  intersect: false,
  mode: "index",
  // Opaque ink. The default tooltip fades in over ~400ms, and a half-opacity
  // dark box on a white card reads as a grey smear with unreadable text — which
  // is exactly what it did. Full alpha plus no opacity animation (below) means
  // it is either absent or legible, never mid-dissolve.
  backgroundColor: "rgba(11,11,11,0.94)",
  titleColor: "#ffffff",
  bodyColor: "#ffffff",
  padding: { top: 8, bottom: 8, left: 10, right: 12 },
  cornerRadius: 6,
  titleFont: { size: 11, weight: "600" },
  bodyFont: { size: 11 },
  bodySpacing: 4,
  caretSize: 5,
  displayColors: true,
  // A short stroke keys each row to its series. `boxHeight` must match the
  // width under `usePointStyle`, or the marker renders off its own baseline.
  usePointStyle: true,
  boxWidth: 8,
  boxHeight: 8,
  boxPadding: 4,
  callbacks: {
    label: (ctx) => {
      const value = Number(ctx.parsed.y) || 0;
      const name = ctx.dataset.label || "";
      // "1 play" rather than "1 plays" — the singular is the common case on a
      // quiet day, which is most days on a young product.
      const noun = unit && value === 1 ? unit.replace(/s$/, "") : unit;
      return `${name}: ${value.toLocaleString()}${noun ? ` ${noun}` : ""}`;
    },
  },
});

/**
 * A ceiling that sits just above the data, on a round number.
 *
 * Left to itself, Chart.js picks an axis maximum by rounding the peak up to its
 * own idea of a nice number — which on a peak of ~1,030 gives **1,500**, so a
 * third of the plot is empty and every bar reads a third shorter than it is.
 * That is a real misreading, not just wasted space.
 *
 * The fix is to round the **step**, not the ceiling — rounding the ceiling is
 * what overshoots, since 1,030 has no round ceiling nearer than 1,500. Step and
 * tick count are chosen together, so every gridline is a round number *and* the
 * top one sits close to the peak: 1,030 becomes 0 / 250 / 500 / 750 / 1k / 1.3k.
 *
 * `DashboardCharts.test.js` pins the invariants across the realistic range —
 * this has been got wrong twice, each time only visible on real data.
 */
export function niceCeiling(peak) {
  if (!Number.isFinite(peak) || peak <= 0) return { max: 1, ticks: 1 };

  // The step is what gets rounded, not the ceiling. Rounding the *ceiling* to a
  // round number is what overshoots: a peak of 1,030 has no round ceiling
  // nearer than 2,000 (49% of the plot empty, every bar reading a third short),
  // whereas a round **step** of 250 gives a ceiling of 1,250 — every gridline
  // still round, and only 18% headroom.
  let best = null;

  for (const count of [3, 4, 5, 2]) {
    const rough = peak / count;
    const magnitude = 10 ** Math.floor(Math.log10(rough));

    for (const multiple of [1, 2, 2.5, 5, 10]) {
      let step = multiple * magnitude;

      // Both axes here are labelled by `compact()`, which renders whole
      // numbers — so a fractional step does not produce a finer axis, it
      // produces a wrong one: a 0.25 step labels itself "0 / 0 / 1 / 1 / 1",
      // five gridlines showing three distinct numbers. Rounding the step up to
      // a whole number is therefore unconditional, not a count-axis special
      // case; the caller's `integer` flag only drives Chart.js's `precision`.
      step = Math.ceil(step);
      if (step * count < peak) continue;

      const max = step * count;
      if (max < peak) continue;

      if (!best || max < best.max) best = { max, ticks: count };
      break;
    }
  }

  return best || { max: peak, ticks: 4 };
}

/** Axes: hairline grid, muted ticks, integers only on a count scale. */
const scales = ({ showX, integer, peak }) => {
  const { max, ticks } = niceCeiling(peak);

  return {
    x: {
      grid: { display: false },
      border: { color: INK.grid },
      ticks: {
        display: showX,
        color: INK.muted,
        font: { size: 10 },
        // Never rotate: a slanted date label is harder to read than a missing
        // one, and `autoSkip` already drops whichever labels will not fit. At
        // phone width that leaves roughly every fourth day, which is enough to
        // locate a bar; the tooltip carries the exact date.
        maxRotation: 0,
        autoSkip: true,
        autoSkipPadding: 16,
      },
    },
    y: {
      beginAtZero: true,
      // An all-zero fortnight would otherwise draw a 0–1 axis with a fractional
      // tick; `max` keeps it a clean empty chart.
      max,
      grid: { color: INK.grid, drawTicks: false },
      border: { display: false },
      ticks: {
        color: INK.muted,
        font: { size: 10 },
        padding: 6,
        // Fixed step, so every gridline is the round number the ceiling was
        // chosen to guarantee rather than whatever a tick budget produces.
        stepSize: max / ticks,
        precision: integer ? 0 : undefined,
        callback: (value) => compact(value),
      },
    },
  };
};

const baseOptions = ({ showX, integer, unit, peak }) => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index", intersect: false },
  // The tooltip must not fade: a partly-transparent dark box over a white card
  // is the grey smear this replaced. Everything else may animate.
  animation: { duration: 240 },
  plugins: { legend: { display: false }, tooltip: tooltip(unit) },
  scales: scales({ showX, integer, peak }),
});

/**
 * Two measures over the same days, as two aligned plots.
 *
 * Explicitly **not** one plot with two y-axes. The count is a column chart
 * because a day's completions are a discrete quantity that starts at zero; the
 * points are an area because what matters about them is the shape of the trend,
 * and an area at 10% opacity reads as context beneath its own number rather
 * than competing with the bars above it.
 *
 * The x-axis is drawn once, under the lower plot, and the upper plot's grid
 * lines up with it — so a column and the point-value below it are read as the
 * same day without either chart claiming the other's scale.
 */
export const DualMeasureDays = ({
  days,
  countKey,
  countLabel,
  countUnit,
  pointsLabel = "Points",
  accent = SERIES.count,
}) => {
  const labels = useMemo(() => days.map((d) => shortDay(d.day)), [days]);
  const counts = useMemo(() => days.map((d) => Number(d[countKey]) || 0), [days, countKey]);
  const points = useMemo(() => days.map((d) => Number(d.points) || 0), [days]);

  const countData = {
    labels,
    datasets: [
      {
        label: countLabel,
        data: counts,
        backgroundColor: accent,
        // 4px rounded data-end, square at the baseline.
        borderRadius: { topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0 },
        borderSkipped: "bottom",
        // Never fill the slot — the leftover band is air, not data.
        maxBarThickness: 22,
        categoryPercentage: 0.72,
        barPercentage: 0.92,
      },
    ],
  };

  const pointsData = {
    labels,
    datasets: [
      {
        label: pointsLabel,
        data: points,
        borderColor: SERIES.points,
        backgroundColor: (ctx) => {
          const { ctx: c, chartArea } = ctx.chart;
          if (!chartArea) return "rgba(235,104,52,0.10)";
          const g = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          g.addColorStop(0, "rgba(235,104,52,0.22)");
          g.addColorStop(1, "rgba(235,104,52,0.01)");
          return g;
        },
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        pointBackgroundColor: SERIES.points,
        // The 2px surface ring, so a hovered dot stays legible over the fill.
        pointHoverBorderColor: "#ffffff",
        pointHoverBorderWidth: 2,
        tension: 0.35,
        fill: true,
      },
    ],
  };

  return (
    <div>
      {/* A legend for two series, keyed the way each mark is drawn: a swatch
          for the columns, a stroke for the line. */}
      <div className="flex items-center gap-4 mb-3 text-[11px] text-gray-500">
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: accent }} />
          {countLabel}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-0.5 rounded-full" style={{ backgroundColor: SERIES.points }} />
          {pointsLabel}
        </span>
      </div>

      {/* Heights are fixed because Chart.js with `maintainAspectRatio: false`
          fills its container, and a container sized by its content collapses.
          They step up past `sm` so a phone gets a readable chart rather than a
          desktop chart squeezed into a third of the width. */}
      <div className="h-28 sm:h-32">
        <Bar
          data={countData}
          options={baseOptions({
            showX: false,
            integer: true,
            unit: countUnit,
            peak: Math.max(0, ...counts),
          })}
        />
      </div>
      <div className="h-16 sm:h-20 -mt-1">
        <Line
          data={pointsData}
          options={baseOptions({
            showX: true,
            integer: false,
            unit: "pts",
            peak: Math.max(0, ...points),
          })}
        />
      </div>
    </div>
  );
};

/**
 * A ranked list of nominal categories.
 *
 * One hue for every row, deliberately. Darkening the bar as the value grows
 * would double-encode length as lightness — spending the only free channel on
 * something the bar already says, and on categories (task types, games) that
 * have no natural order to encode in the first place.
 *
 * The value is direct-labelled at the tip, so nothing here depends on reading a
 * length against an absent axis, and the secondary line carries the figures
 * that would otherwise need a second chart.
 */
export const RankedBars = ({ rows, accent = SERIES.count, emptyText }) => {
  if (!rows || rows.length === 0) {
    return <p className="py-8 text-center text-xs text-gray-400">{emptyText}</p>;
  }

  const peak = Math.max(1, ...rows.map((r) => Number(r.value) || 0));

  return (
    <div className="space-y-2.5">
      {rows.map((row) => (
        <div key={row.key}>
          <div className="flex items-baseline justify-between gap-3 mb-1">
            <span className="text-xs text-gray-700 truncate flex items-center gap-1.5 min-w-0">
              <span className="truncate">{row.label}</span>
              {row.muted && (
                <span className="text-[9px] uppercase tracking-wide text-gray-400 border border-gray-200 rounded px-1 shrink-0">
                  {row.muted}
                </span>
              )}
            </span>
            <span className="text-xs font-semibold text-gray-900 tabular-nums shrink-0">
              {Number(row.value || 0).toLocaleString()}
            </span>
          </div>

          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${Math.max(3, (Number(row.value) / peak) * 100)}%`,
                backgroundColor: accent,
              }}
            />
          </div>

          {row.foot && <p className="text-[10px] text-gray-400 mt-1">{row.foot}</p>}
        </div>
      ))}
    </div>
  );
};

/**
 * A 12-point sparkline for a stat tile.
 *
 * No axes and no labels: it is a shape, not a chart, and the tile's own value
 * is the number. It exists to say "and this is the direction" in the space of a
 * few pixels, which a second full chart could not justify.
 */
export const Sparkline = ({ values, color = SERIES.count }) => {
  const series = (values || []).map((v) => Number(v) || 0);
  if (series.length < 2) return null;

  const data = {
    labels: series.map((_, i) => i),
    datasets: [
      {
        data: series,
        borderColor: color,
        borderWidth: 1.75,
        pointRadius: 0,
        tension: 0.4,
        fill: false,
      },
    ],
  };

  return (
    <div className="h-8 w-full" aria-hidden="true">
      <Line
        data={data}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false }, tooltip: { enabled: false } },
          scales: { x: { display: false }, y: { display: false } },
          elements: { line: { capBezierPoints: true } },
        }}
      />
    </div>
  );
};
